import { useState, useEffect } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdAdd, MdDelete, MdEdit, MdCheck, MdClose } from 'react-icons/md';

function ManageSection({ title, endpoint, fields }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchItems = async () => {
    try {
      const res = await api.get(endpoint);
      setItems(res.data.data);
    } catch (err) {
      toast.error(`Failed to load ${title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post(endpoint, form);
      toast.success(`${title.slice(0, -1)} added`);
      setForm({});
      setShowForm(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete this ${title.slice(0, -1).toLowerCase()}?`)) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    const ef = {};
    fields.forEach(f => { ef[f.key] = item[f.key] || ''; });
    setEditForm(ef);
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`${endpoint}/${id}`, editForm);
      toast.success('Updated');
      setEditingId(null);
      fetchItems();
    } catch (err) {
      toast.error('Update failed');
    }
  };

  return (
    <div className="manage-section">
      <div className="section-header">
        <h3>{title}</h3>
        <button className="btn btn-sm btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? <><MdClose /> Cancel</> : <><MdAdd /> Add</>}
        </button>
      </div>

      {showForm && (
        <form className="manage-form" onSubmit={handleCreate}>
          {fields.map(f => (
            <input
              key={f.key}
              type="text"
              placeholder={f.placeholder}
              value={form[f.key] || ''}
              onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
              required={f.required}
            />
          ))}
          <button type="submit" className="btn btn-sm btn-primary">Add</button>
        </form>
      )}

      {loading ? <p className="text-muted">Loading...</p> : (
        <div className="manage-list">
          {items.map(item => (
            <div key={item.id} className="manage-item">
              {editingId === item.id ? (
                <div className="manage-edit-row">
                  {fields.map(f => (
                    <input
                      key={f.key}
                      type="text"
                      value={editForm[f.key] || ''}
                      onChange={e => setEditForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    />
                  ))}
                  <button className="btn btn-sm btn-primary" onClick={() => saveEdit(item.id)}><MdCheck /></button>
                  <button className="btn btn-sm btn-ghost" onClick={() => setEditingId(null)}><MdClose /></button>
                </div>
              ) : (
                <div className="manage-display-row">
                  <div className="manage-item-info">
                    <span className="manage-name">{item.name}</span>
                    {item.state && <span className="manage-sub">{item.state}</span>}
                    {item.description && <span className="manage-sub">{item.description}</span>}
                    <span className="manage-slug">{item.slug}</span>
                  </div>
                  <div className="action-btns">
                    <button className="btn btn-sm btn-ghost" onClick={() => startEdit(item)}><MdEdit /></button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(item.id)}><MdDelete /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {items.length === 0 && <p className="text-muted">No {title.toLowerCase()} added yet</p>}
        </div>
      )}
    </div>
  );
}

export default function Manage() {
  return (
    <div className="page">
      <div className="page-header">
        <h2>Manage Categories & Cities</h2>
      </div>

      <div className="manage-grid">
        <div className="card">
          <ManageSection
            title="Categories"
            endpoint="/categories"
            fields={[
              { key: 'name', placeholder: 'Category name (e.g. Markets)', required: true },
              { key: 'description', placeholder: 'Description (optional)', required: false },
            ]}
          />
        </div>

        <div className="card">
          <ManageSection
            title="Cities"
            endpoint="/cities"
            fields={[
              { key: 'name', placeholder: 'City name (e.g. Mumbai)', required: true },
              { key: 'state', placeholder: 'State (e.g. Maharashtra)', required: false },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
